import { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileText, Download, Trash2, Loader2, Paperclip, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate, fileSize } from "@/lib/format";

const DOC_TYPES = ["Avtale", "Sluttseddel/SPA", "Protokoll", "Vedtekter", "Aksjonæravtale", "Årsregnskap", "Styredokument", "Annet"];

export default function Documents() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("Avtale");
  const [counterparty, setCounterparty] = useState("");
  const inputRef = useRef();

  const load = useCallback(() => api.get(`/companies/${companyId}/documents`).then((r) => setDocs(r.data)), [companyId]);

  useEffect(() => {
    setDocs(null);
    load();
  }, [load]);

  const openDialog = () => {
    setFile(null);
    setCategory("Avtale");
    setCounterparty("");
    setOpen(true);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Velg en fil å laste opp");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ category, counterparty: counterparty.trim() });
      await api.post(`/companies/${companyId}/documents?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument lastet opp");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const download = (d) => {
    openSigned(`/documents/${d.id}/download-link`).catch(() => toast.error("Kunne ikke laste ned dokumentet"));
  };

  const remove = async (d) => {
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Dokument slettet");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Dokumenter" description="Alle styredokumenter, protokoller og selskapsfiler – merket med type og motpart for full oversikt.">
        <Button variant="outline" onClick={() => navigate(`/selskap/${companyId}/aksjonaeravtale`)} className="gap-2 active:scale-[0.98]" data-testid="create-shareholder-agreement-btn">
          <FileSignature className="h-4 w-4" /> Lag aksjonæravtale
        </Button>
        <Button onClick={openDialog} className="gap-2 active:scale-[0.98]" data-testid="upload-btn">
          <Upload className="h-4 w-4" /> Last opp
        </Button>
      </PageHeader>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
        {docs === null ? (
          <div className="space-y-1 p-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
        ) : docs.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Ingen dokumenter ennå.</div>
        ) : (
          <div className="divide-y divide-border/60">
            {docs.map((d, i) => (
              <motion.div key={d.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} data-testid={`doc-row-${d.id}`} className="group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/50">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium">{d.name}</p>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary" data-testid={`doc-category-${d.id}`}>{d.category}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {d.counterparty ? <span data-testid={`doc-counterparty-${d.id}`}>Motpart: {d.counterparty} · </span> : null}
                    {fileSize(d.size)} · {formatDate(d.created_at)} · {d.uploaded_by_name}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button variant="ghost" size="icon" onClick={() => download(d)} data-testid={`download-${d.id}`}><Download className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(d)} data-testid={`delete-doc-${d.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Last opp dokument</DialogTitle>
            <DialogDescription>Velg fil og fyll ut dokumenttype og motpart, så blir listen oversiktlig.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Fil</Label>
              <input ref={inputRef} type="file" className="hidden" data-testid="file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button
                type="button"
                data-testid="pick-file-btn"
                onClick={() => inputRef.current?.click()}
                className="mt-1.5 flex w-full items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60"
              >
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className={`truncate ${file ? "font-medium" : "text-muted-foreground"}`}>{file ? file.name : "Velg fil …"}</span>
              </button>
            </div>
            <div>
              <Label className="text-xs">Dokumenttype</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1.5" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Motpart (valgfritt)</Label>
              <Input className="mt-1.5" data-testid="doc-counterparty-input" value={counterparty} onChange={(e) => setCounterparty(e.target.value)} placeholder="f.eks. kjøper, leverandør eller aksjonær" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
            <Button onClick={upload} disabled={uploading || !file} className="gap-2" data-testid="confirm-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Last opp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
